import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { notify } from "@/common/components/common/Notification";
import IPOService from "@/services/IPO/APIs/IPOService";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { Checkbox } from "@/common/components/ui/checkbox";
import Loader from "@/common/components/ui/loader";
import {
  CreateMemberPayload,
  UpdateMemberPayload,
} from "@/utils/types/IPO/ipo";
import { cn } from "@/lib/utils";

type FormErrors = Partial<Record<keyof CreateMemberPayload, string>>;
const MOBILE_REGEX = /^\d{10}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const UPI_REGEX = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;

const CreateMemberPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [loading, setLoading] = useState(isEdit || isViewMode);
  const [formData, setFormData] = useState<CreateMemberPayload>({
    ipoUserID: 0,
    clientName: "",
    depository: "CDSL",
    dpId: "",
    upi: "",
    upiVerified: false,
    pan: "",
    mobile: "",
    groupCode: "Self",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const fetchMember = async () => {
    try {
      setLoading(true);
      // Fallback: If there's no get-by-id API yet, we fetch the list and find the member.
      const response = await IPOService.getMemberListAdminList();
      const member = response.results.find(m => m.id === id);
      if (member) {
        setFormData({
          ipoUserID: member.ipoUserID,
          clientName: member.clientName,
          depository: member.depository ?? "NSDL",
          dpId: member.dpId,
          upi: member.upi,
          upiVerified: member.upiVerified,
          pan: member.pan,
          mobile: member.mobile,
          groupCode: member.groupCode ?? "Self",
        });
      } else {
        notify.error("Member not found in the list");
        navigate("/admin/ipo/member-management");
      }
    } catch (error: any) {
      console.error(error);
      notify.error(error?.message ?? "Failed to fetch member details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchMember();
    }
  }, [id, isEdit, isViewMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let finalValue: string | number = value;

    if (name === "ipoUserID") {
      finalValue = value ? Number(value) : 0;
    } else if (name === "mobile") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "dpId" || name === "pan" || name === "upi") {
      // Note: UPI usually keeping as lowercase is preferred, but pan/dpId should be uppercase
      finalValue = name === "upi" ? value.toLowerCase() : value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue,
    }));

    // Keep submit-based validation, but refresh field validation on change.
    setErrors(prev => {
      if (name === "mobile") {
        const mobile = String(finalValue ?? "").trim();
        const mobileError = !mobile
          ? "Mobile Number is required"
          : !MOBILE_REGEX.test(mobile)
            ? "Enter valid Mobile Number (10 digits)"
            : undefined;
        return { ...prev, mobile: mobileError };
      }

      if (name === "pan") {
        const pan = String(finalValue ?? "")
          .trim()
          .toUpperCase();
        const panError = !pan
          ? "PAN Number is required"
          : !PAN_REGEX.test(pan)
            ? "Enter valid PAN Number (e.g. ABCDE1234F)"
            : undefined;
        return { ...prev, pan: panError };
      }

      if (name === "upi") {
        const upi = String(finalValue ?? "").trim();
        const upiError = !upi
          ? "UPI ID is required"
          : !UPI_REGEX.test(upi)
            ? "Enter valid UPI ID (e.g. user@okaxis)"
            : undefined;
        return { ...prev, upi: upiError };
      }

      return prev;
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === "depository") {
        if (value === "NSDL") {
          if (!prev.dpId?.startsWith("IN")) {
            newData.dpId = "IN";
          }
        } else if (value === "CDSL") {
          if (prev.dpId === "IN") {
            newData.dpId = "";
          }
        }
      }

      return newData;
    });
  };

  const validateFields = (): FormErrors => {
    const nextErrors: FormErrors = {};
    const mobile = String(formData.mobile ?? "").trim();
    const pan = String(formData.pan ?? "")
      .trim()
      .toUpperCase();
    const upi = String(formData.upi ?? "").trim();

    if (!String(formData.clientName ?? "").trim()) {
      nextErrors.clientName = "Client Name is required";
    }
    if (!mobile) {
      nextErrors.mobile = "Mobile Number is required";
    } else if (!MOBILE_REGEX.test(mobile)) {
      nextErrors.mobile = "Enter valid Mobile Number (10 digits)";
    }
    if (!pan) {
      nextErrors.pan = "PAN Number is required";
    } else if (!PAN_REGEX.test(pan)) {
      nextErrors.pan = "Enter valid PAN Number (e.g. ABCDE1234F)";
    }
    if (!String(formData.depository ?? "").trim()) {
      nextErrors.depository = "Depository is required";
    }
    if (!String(formData.dpId ?? "").trim()) {
      nextErrors.dpId = "DP ID is required";
    }
    if (!String(formData.ipoUserID ?? "").trim()) {
      nextErrors.ipoUserID = "Primary Member ID is required";
    }
    if (!upi) {
      nextErrors.upi = "UPI ID is required";
    } else if (!UPI_REGEX.test(upi)) {
      nextErrors.upi = "Enter valid UPI ID (e.g. user@okaxis)";
    }
    if (!String(formData.groupCode ?? "").trim()) {
      nextErrors.groupCode = "Member Group is required";
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      return;
    }

    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      // Check if any error is due to a missing required field
      const hasMissingRequired = Object.keys(fieldErrors).some(key => {
        const value = formData[key as keyof CreateMemberPayload];
        return (
          value === "" ||
          value === null ||
          value === undefined ||
          (key === "ipoUserID" && value === 0)
        );
      });

      if (hasMissingRequired) {
        notify.error("Please fill all required fields.");
      }
      return;
    }

    if (formData.dpId) {
      if (formData.dpId.length !== 16) {
        notify.error("DP ID must be exactly 16 characters long");
        return;
      }
      if (formData.depository === "NSDL" && !formData.dpId.startsWith("IN")) {
        notify.error("For NSDL, DP ID must start with IN");
        return;
      }
    }

    try {
      setLoading(true);

      if (isEdit) {
        const updatePayload: UpdateMemberPayload = {
          id: id!,
          ...formData,
        };
        const res: any = await IPOService.updateMember(id!, updatePayload);
        if (res.success) {
          notify.success("", res?.message ?? "");
        } else {
          notify.error("", res?.message ?? "");
        }
      } else {
        const res: any = await IPOService.createMember(formData);
        if (res.success) {
          notify.success("", res?.message ?? "");
        } else {
          notify.error("", res?.message ?? "");
        }
      }
      navigate("/admin/ipo/member-management");
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to save member details";
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && (isEdit || isViewMode)) {
    return <Loader message="Loading Member Data..." />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      {loading && !isEdit && !isViewMode && (
        <Loader message="Saving Member..." />
      )}
      <form onSubmit={handleSubmit} noValidate className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => navigate("/admin/ipo/member-management")}
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Member : ${formData.clientName}`
                      : isEdit
                        ? `Edit Member : ${formData.clientName}`
                        : "Create New Member"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure member identity, depository, and UPI details
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/ipo/member-management")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Member"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="shadow-sm">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="clientName">
                        Client Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="clientName"
                        name="clientName"
                        value={formData.clientName ?? ""}
                        onChange={handleInputChange}
                        placeholder="e.g. Jane Smith"
                        className={cn(
                          "placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.clientName &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                      />
                      {errors.clientName && (
                        <p className="text-xs text-destructive">
                          {errors.clientName}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mobile">
                        Mobile Number{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="mobile"
                        name="mobile"
                        inputMode="numeric"
                        value={formData.mobile ?? ""}
                        onChange={handleInputChange}
                        placeholder="e.g. 9123456789"
                        className={cn(
                          "placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.mobile &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                        maxLength={10}
                      />
                      {errors.mobile && (
                        <p className="text-xs text-destructive">
                          {errors.mobile}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pan">
                        PAN Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="pan"
                        name="pan"
                        value={formData.pan ?? ""}
                        onChange={handleInputChange}
                        placeholder="e.g. FGHIJ5678A"
                        className={cn(
                          "uppercase placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.pan &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                        maxLength={10}
                      />
                      {errors.pan && (
                        <p className="text-xs text-destructive">{errors.pan}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Depository Details
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="depository">
                        Depository <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.depository ?? "CDSL"}
                        onValueChange={val =>
                          handleSelectChange("depository", val)
                        }
                        disabled={isViewMode}
                      >
                        <SelectTrigger
                          className={cn(
                            errors.depository &&
                              "border-destructive focus:ring-destructive/20"
                          )}
                        >
                          <SelectValue placeholder="Select Depository" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDSL">CDSL</SelectItem>
                          <SelectItem value="NSDL">NSDL</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.depository && (
                        <p className="text-xs text-destructive">
                          {errors.depository}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dpId">
                        DP ID <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="dpId"
                        name="dpId"
                        value={formData.dpId ?? ""}
                        onChange={handleInputChange}
                        placeholder={
                          formData.depository === "NSDL"
                            ? "e.g. IN30000012345678"
                            : "e.g. 1200000012345678"
                        }
                        className={cn(
                          "uppercase placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.dpId &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                        maxLength={16}
                        minLength={16}
                      />
                      {errors.dpId && (
                        <p className="text-xs text-destructive">
                          {errors.dpId}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ipoUserID">
                        Primary Member ID{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="ipoUserID"
                        name="ipoUserID"
                        type="number"
                        value={formData.ipoUserID ?? ""}
                        onChange={handleInputChange}
                        placeholder="e.g. 11111111"
                        className={cn(
                          "placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.ipoUserID &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                      />
                      {errors.ipoUserID && (
                        <p className="text-xs text-destructive">
                          {errors.ipoUserID}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Application Details
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="upi">
                        UPI ID <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="upi"
                        name="upi"
                        value={formData.upi ?? ""}
                        onChange={handleInputChange}
                        placeholder="e.g. user@okhdfcbank"
                        className={cn(
                          "placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400",
                          errors.upi &&
                            "border-destructive focus-visible:ring-destructive/20"
                        )}
                        disabled={isViewMode}
                      />
                      {errors.upi && (
                        <p className="text-xs text-destructive">{errors.upi}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="groupCode">
                        Member Group <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.groupCode ?? "Self"}
                        onValueChange={val =>
                          handleSelectChange("groupCode", val)
                        }
                        disabled={isViewMode}
                      >
                        <SelectTrigger
                          className={cn(
                            errors.groupCode &&
                              "border-destructive focus:ring-destructive/20"
                          )}
                        >
                          <SelectValue placeholder="Select Group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Self">Self</SelectItem>
                          <SelectItem value="Family">Family</SelectItem>
                          <SelectItem value="Friends">Friends</SelectItem>
                          <SelectItem value="Office">Office</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.groupCode && (
                        <p className="text-xs text-destructive">
                          {errors.groupCode}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col justify-end space-y-2">
                      <div className="flex items-center space-x-2 py-2">
                        <Checkbox
                          id="upiVerified"
                          checked={formData.upiVerified}
                          onCheckedChange={checked =>
                            setFormData(prev => ({
                              ...prev,
                              upiVerified: !!checked,
                            }))
                          }
                          disabled={isViewMode}
                        />
                        <Label
                          htmlFor="upiVerified"
                          className="cursor-pointer font-medium"
                        >
                          Mark UPI as Verified?
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateMemberPage;
